/**
 * Investor Trends - Active investors, engagement, onboarding, and first engagement metrics
 *
 * Route: /admin/reporting/investor-engagement
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  LabelList,
} from 'recharts'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Select, type SelectOption } from '@/common/components/select'
import { Multiselect, type MultiselectOption } from '@/common/components/multiselect'
import { DatePicker } from '@/common/components/date-picker'
import { useAuth } from '@/common/hooks/useAuth'
import {
  useInvestorEngagement,
  useMarkets,
  useInvestorEngagementBreakdown,
  useInvestorEngagementRecords,
} from '@/features/reporting/hooks/useReportingData'
import {
  fetchInvestorEngagementRecords,
  type InvestorEngagementDataPoint,
} from '@/features/reporting/api/reportingApi'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { incompleteOffset } from '@/features/reporting/utils/periodUtils'
import { ChartModal, ExpandChartButton } from '@/features/reporting/components/ChartModal'
import { Tooltip as InfoTooltip } from '@/common/components/tooltip'
import { series, chrome, semantic, table, tooltipStyle, tickProps } from '@/features/reporting/chartPalette'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: InvestorEngagementPage,
  validateSearch: (search: Record<string, unknown>) => ({
    granularity: (['month', 'week', 'day'].includes(search.granularity as string)
      ? (search.granularity as Granularity) : 'week'),
    dateRange: (['1y', '6mo', '3mo', '1mo', 'custom'].includes(search.dateRange as string)
      ? (search.dateRange as DateRangePreset) : '3mo'),
    market: (search.market as string) || '',
    metric: (['active', 'engaged', 'newOnboarded', 'firstTime'].includes(search.metric as string)
      ? (search.metric as SelectedMetric) : 'active'),
  }),
})

// =============================================================================
// Types
// =============================================================================

type Granularity = 'month' | 'week' | 'day'
type SelectedMetric = 'active' | 'engaged' | 'newOnboarded' | 'firstTime'
type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
]

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function calcDelta(current: number, prior: number | undefined): number | null {
  if (prior === undefined || prior === 0) return null
  return ((current - prior) / prior) * 100
}

function deltaColor(delta: number | null): string {
  if (delta === null) return semantic.neutral
  if (delta > 0) return semantic.positive
  if (delta < 0) return semantic.negative
  return semantic.neutral
}

function deltaLabel(delta: number | null, granLabel: string): string {
  if (delta === null) return `no prior ${granLabel}`
  if (delta === 0) return `0.0%`
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function deltaIndicator(delta: number | null): string {
  if (delta === null) return '\u2014 '
  if (delta > 0) return '\u25B2 '
  if (delta < 0) return '\u25BC '
  return '= '
}

/** Parse a yyyy-MM-dd string as a local-time Date (avoids UTC midnight timezone shift) */
function parseDateAsLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Convert API period strings to readable date labels. */
function formatPeriodLabel(period: string): string {
  const weekMatch = period.match(/^(\d{4})-W(\d{2})$/)
  if (weekMatch) {
    const year = Number(weekMatch[1])
    const week = Number(weekMatch[2])
    const jan4 = new Date(year, 0, 4)
    const dayOfWeek = jan4.getDay() || 7
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7)
    return format(monday, 'MMM d')
  }
  const monthMatch = period.match(/^(\d{4})-(\d{2})$/)
  if (monthMatch) {
    const d = new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1)
    return format(d, "MMM ''yy")
  }
  const dayMatch = period.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dayMatch) {
    const d = new Date(Number(dayMatch[1]), Number(dayMatch[2]) - 1, Number(dayMatch[3]))
    return format(d, 'MMM d')
  }
  return period
}

// =============================================================================
// Chart Data Helpers
// =============================================================================

function getChartData(
  metric: SelectedMetric,
  engagementData: InvestorEngagementDataPoint[] | undefined
): Array<{ period: string; value: number }> {
  if (!engagementData) return []

  let raw: Array<{ period: string; value: number }> = []
  switch (metric) {
    case 'active':
      raw = engagementData.map((d) => ({ period: d.period, value: d.active_investors }))
      break
    case 'engaged':
      raw = engagementData.map((d) => ({ period: d.period, value: d.unique_engaged_investors }))
      break
    case 'newOnboarded':
      raw = engagementData.map((d) => ({ period: d.period, value: d.new_investors_onboarded }))
      break
    case 'firstTime':
      raw = engagementData.map((d) => ({ period: d.period, value: d.first_time_engagements }))
      break
  }
  return [...raw].sort((a, b) => a.period.localeCompare(b.period))
}

function getLatestValue(
  metric: SelectedMetric,
  engagementData: InvestorEngagementDataPoint[] | undefined,
  off: 0 | 1 = 0
): number {
  if (!engagementData || engagementData.length < (1 + off)) return 0
  const latest = engagementData[engagementData.length - 1 - off]

  switch (metric) {
    case 'active':
      return latest.active_investors
    case 'engaged':
      return latest.unique_engaged_investors
    case 'newOnboarded':
      return latest.new_investors_onboarded
    case 'firstTime':
      return latest.first_time_engagements
  }
}

function getPriorValue(
  metric: SelectedMetric,
  engagementData: InvestorEngagementDataPoint[] | undefined,
  off: 0 | 1 = 0
): number | undefined {
  if (!engagementData || engagementData.length < (2 + off)) return undefined
  const prior = engagementData[engagementData.length - 2 - off]

  switch (metric) {
    case 'active':
      return prior.active_investors
    case 'engaged':
      return prior.unique_engaged_investors
    case 'newOnboarded':
      return prior.new_investors_onboarded
    case 'firstTime':
      return prior.first_time_engagements
  }
}

/** Breakdown label showing value + % of total */
function BreakdownValueLabel(props: { x?: string | number; y?: string | number; width?: string | number; height?: string | number; value?: string | number | boolean | null; total: number }) {
  const x = typeof props.x === 'number' ? props.x : 0
  const y = typeof props.y === 'number' ? props.y : 0
  const width = typeof props.width === 'number' ? props.width : 0
  const height = typeof props.height === 'number' ? props.height : 0
  const value = typeof props.value === 'number' ? props.value : 0
  const { total } = props
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
  return (
    <text x={x + width + 6} y={y + height / 2 + 4} fontSize={12} fill={table.headerText}>
      {value.toLocaleString()} ({pct}%)
    </text>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function InvestorEngagementPage() {
  const { token } = useAuth()
  const searchParams = Route.useSearch()
  const { granularity, dateRange, market, metric: selectedMetric } = searchParams
  const navigate = useNavigate()

  const updateSearch = useCallback(
    (updates: Partial<typeof searchParams>) => {
      void navigate({ to: '.', search: { ...searchParams, ...updates }, replace: true })
    },
    [navigate, searchParams]
  )

  const setGranularity = useCallback((val: Granularity) => updateSearch({ granularity: val }), [updateSearch])
  const setMarket = useCallback((vals: string[]) => updateSearch({ market: vals.join(',') }), [updateSearch])
  const marketParam = market || undefined
  const setSelectedMetric = useCallback((val: SelectedMetric) => updateSearch({ metric: val }), [updateSearch])
  const setDateRange = useCallback((val: DateRangePreset) => updateSearch({ dateRange: val }), [updateSearch])

  const [drillDownPeriod, setDrillDownPeriod] = useState<string | null>(null)
  const [chartExpanded, setChartExpanded] = useState(false)
  const [drillDownPage, setDrillDownPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  useEffect(() => {
    setDrillDownPeriod(null)
  }, [granularity, market, dateRange])

  useEffect(() => {
    setDrillDownPage(1)
  }, [drillDownPeriod])

  useEffect(() => {
    setDrillDownPage(1)
  }, [pageSize])

  // ---------------------------------------------------------------------------
  // Data Hooks
  // ---------------------------------------------------------------------------
  const { data: engagementRes } = useInvestorEngagement(granularity, marketParam, startDateStr, endDateStr)
  const { data: marketsRes } = useMarkets()

  const { data: breakdownRes } = useInvestorEngagementBreakdown(
    drillDownPeriod,
    granularity,
    marketParam,
    startDateStr,
    endDateStr,
    selectedMetric
  )

  const { data: recordsRes, isFetching: isRecordsFetching } = useInvestorEngagementRecords(
    drillDownPeriod,
    granularity,
    marketParam,
    startDateStr,
    endDateStr,
    drillDownPage,
    pageSize,
    selectedMetric
  )

  // ---------------------------------------------------------------------------
  // Derived Data
  // ---------------------------------------------------------------------------
  const engagementDataRaw = engagementRes?.data
  const engagementData = useMemo(
    () => engagementDataRaw ? [...engagementDataRaw].sort((a, b) => a.period.localeCompare(b.period)) : undefined,
    [engagementDataRaw]
  )

  const marketOptions: MultiselectOption[] = useMemo(
    () => (marketsRes?.data ?? []).map((m) => ({ value: m, label: m })),
    [marketsRes?.data]
  )

  const chartData = useMemo(
    () => getChartData(selectedMetric, engagementData),
    [selectedMetric, engagementData]
  )

  const granularityLabel = granularity === 'month' ? 'month' : granularity === 'week' ? 'week' : 'day'

  const totals = engagementRes?.totals
  const engOff = incompleteOffset(engagementData, granularity)

  const metricCards = useMemo(() => {
    const metrics: Array<{
      key: SelectedMetric
      label: string
      definition: string
      totalValue: string
      totalTooltip: string
      recentValue: string
      delta: number | null
      showTotal: boolean
    }> = [
      {
        key: 'active',
        label: 'Active',
        definition: 'Total investor accounts that have completed onboarding (cumulative). This is a running count — it includes ALL investors who have ever signed the agreement, regardless of whether they have submitted offers/inquiries. It only grows over time. (Source: Platform)',
        totalValue: formatCount(getLatestValue('active', engagementData, engOff)),
        totalTooltip: 'Total onboarded investor accounts as of the most recent complete period',
        recentValue: formatCount(getLatestValue('active', engagementData, engOff)),
        delta: calcDelta(getLatestValue('active', engagementData, engOff), getPriorValue('active', engagementData, engOff)),
        showTotal: false,
      },
      {
        key: 'engaged',
        label: 'Engaged',
        definition: 'Distinct investors who submitted at least one offer or information request during the selected date range. Unlike the "Engaged" status on the Investors page (which is a rolling 90-day window from today), this count reflects the date filters you have selected. (Source: Platform Activity)',
        totalValue: formatCount(totals?.unique_engaged_investors ?? 0),
        totalTooltip: 'Unique investors who engaged at least once across the entire selected time period',
        recentValue: formatCount(getLatestValue('engaged', engagementData, engOff)),
        delta: calcDelta(getLatestValue('engaged', engagementData, engOff), getPriorValue('engaged', engagementData, engOff)),
        showTotal: true,
      },
      {
        key: 'newOnboarded',
        label: 'Onboarded',
        definition: 'NEW investors who completed onboarding (signed the agreement) for the first time in each period. Unlike Active (which is cumulative), this shows only the new additions per period — how many investors signed up that week/month. (Source: Platform)',
        totalValue: formatCount(totals?.new_investors_onboarded ?? 0),
        totalTooltip: 'Total new investors onboarded across the entire selected time period',
        recentValue: formatCount(getLatestValue('newOnboarded', engagementData, engOff)),
        delta: calcDelta(getLatestValue('newOnboarded', engagementData, engOff), getPriorValue('newOnboarded', engagementData, engOff)),
        showTotal: true,
      },
      {
        key: 'firstTime',
        label: 'First Engagement',
        definition: 'Investors who submitted their very first offer or information request in a given period — they had no prior engagement activity. (Source: Platform Activity)',
        totalValue: formatCount(totals?.first_time_engagements ?? 0),
        totalTooltip: 'Total first-time investor engagements across the entire selected time period',
        recentValue: formatCount(getLatestValue('firstTime', engagementData, engOff)),
        delta: calcDelta(getLatestValue('firstTime', engagementData, engOff), getPriorValue('firstTime', engagementData, engOff)),
        showTotal: true,
      },
    ]
    return metrics
  }, [engagementData, totals, engOff])

  // Breakdown totals for % labels — always use sum of breakdown values so percentages
  // reflect each segment's share (investors in multiple markets are counted per market)
  const breakdownTotal = (breakdownRes?.data ?? []).reduce((sum, d) => sum + d.value, 0)

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleChartClick = useCallback(
    (nextState: { activeLabel?: string | number | undefined }) => {
      if (nextState.activeLabel != null) {
        setDrillDownPeriod(String(nextState.activeLabel))
      }
    },
    []
  )

  const handleCloseDrillDown = useCallback(() => {
    setDrillDownPeriod(null)
  }, [])

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange, setDateRange])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
            <div className={styles.controlSelect}>
              <Multiselect label="Market" options={marketOptions} value={market ? market.split(',') : []} onValueChange={setMarket} placeholder="All Markets" size="sm" />
            </div>
            <div className={styles.granularityToggle}>
              <button className={clsx(styles.granularityButton, granularity === 'month' && styles.granularityButtonActive)} onClick={() => setGranularity('month')}>Monthly</button>
              <button className={clsx(styles.granularityButton, granularity === 'week' && styles.granularityButtonActive)} onClick={() => setGranularity('week')}>Weekly</button>
              <button className={clsx(styles.granularityButton, granularity === 'day' && styles.granularityButtonActive)} onClick={() => setGranularity('day')}>Daily</button>
            </div>
            <div className={styles.controlDateGroup}>
              <div className={styles.granularityToggle}>
                {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
                  <button key={preset} className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)} onClick={() => handleDateRangeChange(preset)}>
                    {preset === 'custom' ? 'Custom' : preset}
                  </button>
                ))}
              </div>
              <div className={styles.customDateRange}>
                <div className={styles.customDatePicker}>
                  <DatePicker label="Start" value={parseDateAsLocal(startDateStr)} onChange={setCustomStart} maxDate={parseDateAsLocal(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
                </div>
                <Text variant="bodySm" color="muted">{'\u2013'}</Text>
                <div className={styles.customDatePicker}>
                  <DatePicker label="End" value={parseDateAsLocal(endDateStr)} onChange={setCustomEnd} minDate={parseDateAsLocal(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
                </div>
              </div>
            </div>
          </div>

        {/* Metric Cards */}
        <InfoTooltip.Provider>
        <div className={styles.metricsGrid}>
          {metricCards.map((card) => (
            <div
              key={card.key}
              className={clsx(styles.metricCard, selectedMetric === card.key && styles.metricCardActive)}
              onClick={() => setSelectedMetric(card.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedMetric(card.key)
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Text variant="bodySm" color="muted">{card.label}</Text>
                <InfoTooltip content={card.definition} side="top" delayDuration={200}>
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', flexShrink: 0 }}
                    aria-label={`About ${card.label}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zM8 5a.75.75 0 1 0 0 1.5A.75.75 0 0 0 8 5zm-.75 2.5a.75.75 0 0 0 0 1.5h.25v2.25h-.25a.75.75 0 0 0 0 1.5h2a.75.75 0 0 0 0-1.5H9V7.75A.75.75 0 0 0 8.25 7h-.75a.75.75 0 0 0-.25.5z"/>
                    </svg>
                  </span>
                </InfoTooltip>
              </div>
              <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }} title={card.totalTooltip}>
                {card.totalValue}
              </Text>
              {card.showTotal && (
                <Text variant="bodyXs" color="muted" style={{ marginBottom: '4px' }}>
                  total in period
                </Text>
              )}
              <div className={styles.metricCardSecondary}>
                <Text variant="bodyXs" color="muted">
                  Most recent completed {granularityLabel}:{' '}
                  <strong>{card.recentValue}</strong>
                </Text>
                <Text variant="bodyXs" style={{ color: deltaColor(card.delta) }}>
                  {deltaIndicator(card.delta)}
                  {card.delta !== null
                    ? `${deltaLabel(card.delta, granularityLabel)} vs prior ${granularityLabel}`
                    : deltaLabel(card.delta, granularityLabel)}
                </Text>
              </div>
            </div>
          ))}
        </div>
        </InfoTooltip.Provider>

        {/* Trend Chart */}
        <div className={styles.chartContainer}>
          <div className={styles.chartHeader}>
            <Text variant="bodySm" color="muted">
              {selectedMetric === 'active' && 'Active Investors'}
              {selectedMetric === 'engaged' && 'Engaged Investors'}
              {selectedMetric === 'newOnboarded' && 'Onboarded Investors'}
              {selectedMetric === 'firstTime' && 'First Engagement'}
              {' '}
              ({granularity === 'month' ? 'Monthly' : granularity === 'week' ? 'Weekly' : 'Daily'})
            </Text>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <ExpandChartButton onClick={() => setChartExpanded(true)} />
              <ExportMenu options={[
                { label: 'Chart Data (CSV)', data: chartData, filename: `${selectedMetric}-trend.csv` },
              ]} />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} onClick={handleChartClick}>
              <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
              <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
              <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
              <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={series.primary}
                strokeWidth={2}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dot={((props: any) => {
                  const { cx = 0, cy = 0, index = 0 } = props
                  const interval = Math.max(1, Math.ceil(chartData.length / 8))
                  const isPrimary = index % interval === 0
                  return <circle key={index} cx={cx} cy={cy} r={isPrimary ? 4 : 2} fill={series.primary} cursor="pointer" />
                }) as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                activeDot={{ r: 6, fill: series.primary, stroke: chrome.tooltipBg, strokeWidth: 2 }}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="value"
                  position="top"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  content={((props: any) => {
                    const x = typeof props.x === 'number' ? props.x : 0
                    const y = typeof props.y === 'number' ? props.y : 0
                    const { value, index } = props
                    const interval = Math.max(1, Math.ceil(chartData.length / 8))
                    if (typeof index !== 'number' || index % interval !== 0) return null
                    const val = typeof value === 'number' ? value.toLocaleString() : ''
                    if (!val) return null
                    return <text key={index} x={x} y={y - 10} textAnchor="middle" style={{ fontSize: '13px', fontWeight: 600 }} fill={series.primary}>{val}</text>
                  }) as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>

        <ChartModal
          open={chartExpanded}
          onClose={() => setChartExpanded(false)}
          title={`${selectedMetric === 'active' ? 'Active Investors' : selectedMetric === 'engaged' ? 'Engaged Investors' : selectedMetric === 'newOnboarded' ? 'Onboarded Investors' : 'First Engagement'} (${granularity === 'month' ? 'Monthly' : granularity === 'week' ? 'Weekly' : 'Daily'})`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
              <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
              <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
              <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
              <Line type="monotone" dataKey="value" stroke={series.primary} strokeWidth={2} dot={{ r: 4, fill: series.primary }} activeDot={{ r: 6, fill: series.primary, stroke: chrome.tooltipBg, strokeWidth: 2 }} isAnimationActive={false}>
                <LabelList dataKey="value" position="top" style={{ fontSize: '13px', fontWeight: 600, fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </ChartModal>

        {/* Drill-Down Panel */}
        {drillDownPeriod && (
          <div className={styles.drillDownPanel}>
            <div className={styles.drillDownHeader}>
              <Text variant="body" weight="bold">Breakdown: {formatPeriodLabel(drillDownPeriod)}</Text>
              <Button variant="ghost" size="sm" onClick={handleCloseDrillDown}>
                <Icon name="close" size={18} />
              </Button>
            </div>

            {/* Breakdown Chart */}
            <div style={{ position: 'relative', minHeight: '200px', marginBottom: '24px' }}>
              <div className={styles.chartHeader}>
                <div>
                  <Text variant="bodySm" color="muted">
                    {selectedMetric === 'active' && 'Active Investors'}
                    {selectedMetric === 'engaged' && 'Engaged Investors'}
                    {selectedMetric === 'newOnboarded' && 'Onboarded Investors'}
                    {selectedMetric === 'firstTime' && 'First Engagement'}
                    {' '}by Market
                    {breakdownRes?.total != null && (
                      <span style={{ fontWeight: 600 }}> — {breakdownRes.total.toLocaleString()} total</span>
                    )}
                  </Text>
                  {breakdownTotal > 0 && selectedMetric !== 'engaged' && (
                    <Text variant="bodyXs" color="muted" style={{ marginTop: '2px' }}>
                      Investors in multiple markets are counted once per market
                    </Text>
                  )}
                </div>
                <ExportMenu options={[
                  { label: 'Breakdown (CSV)', data: (breakdownRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `investor-engagement-breakdown-${drillDownPeriod}.csv` },
                ]} />
              </div>
              {!breakdownRes && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {breakdownRes?.data && breakdownRes.data.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(160, breakdownRes.data.length * 40)} style={{ paddingRight: '120px' }}>
                  <BarChart data={breakdownRes.data} layout="vertical" margin={{ right: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} horizontal={false} />
                    <XAxis type="number" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} />
                    <YAxis type="category" dataKey="label" tick={tickProps} tickLine={false} axisLine={false} width={120} />
                    <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} />
                    <Bar dataKey="value" fill={series.primary} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      <LabelList
                        dataKey="value"
                        position="right"
                        content={(props) => (
                          <BreakdownValueLabel {...props} total={breakdownTotal} />
                        )}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Records Table */}
            <div style={{ position: 'relative', minHeight: '300px' }}>
              {(!recordsRes || isRecordsFetching) && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {recordsRes?.data && recordsRes.data.length > 0 && (
                <>
                <div className={styles.chartHeader}>
                  <Text variant="bodySm" weight="bold">
                    Records
                    {recordsRes.pagination && (
                      <Text as="span" variant="bodyXs" color="muted" style={{ marginLeft: '8px', fontWeight: 400 }}>
                        ({recordsRes.pagination.total_count.toLocaleString()} total)
                      </Text>
                    )}
                  </Text>
                  <Stack direction="row" gap="8" align="center">
                    <div className={styles.pageSizeSelect}>
                      <Select
                        label=""
                        size="sm"
                        options={PAGE_SIZE_OPTIONS}
                        value={String(pageSize)}
                        onValueChange={(v) => { setPageSize(Number(v)); setDrillDownPage(1) }}
                        showOptional={false}
                      />
                    </div>
                    <Text variant="bodyXs" color="muted">per page</Text>
                    <ExportMenu options={[
                      { label: 'Export Page', data: (recordsRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `investor-engagement-records-${drillDownPeriod}-page${drillDownPage}.csv` },
                      ...(recordsRes?.pagination && recordsRes.pagination.total_pages > 1 ? [{
                        label: 'Export All Records',
                        description: `${recordsRes.pagination.total_count.toLocaleString()} records — may take a moment`,
                        fetchData: async () => {
                          if (!token || !drillDownPeriod) return []
                          const allData: Record<string, unknown>[] = []
                          const totalPages = recordsRes.pagination!.total_pages
                          for (let p = 1; p <= totalPages; p++) {
                            const res = await fetchInvestorEngagementRecords(token, { period: drillDownPeriod, granularity, market: marketParam, start_date: startDateStr, end_date: endDateStr, page: p, page_size: pageSize, metric: selectedMetric })
                            allData.push(...(res.data as unknown as Record<string, unknown>[]))
                          }
                          return allData
                        },
                        filename: `investor-engagement-records-${drillDownPeriod}-all.csv`,
                      }] : []),
                    ]} />
                  </Stack>
                </div>
                <InfoTooltip.Provider>
                <table className={styles.recordsTable}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px', width: '40px' }}>#</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          Investor Name
                          <InfoTooltip content="The investor's full name from their COM platform profile." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          Company
                          <InfoTooltip content="The company or LLC name from the investor's COM platform profile." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          Markets
                          <InfoTooltip content="The markets this investor has selected as their areas of interest in their COM platform profile." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '3px' }}>
                          Offers
                          <InfoTooltip content="Number of cash offers this investor submitted during the selected period. Source: COM platform offer submissions." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '3px' }}>
                          Questions
                          <InfoTooltip content="Number of information requests (questions about properties) this investor submitted during the selected period. Source: COM platform information requests." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          Last Engagement
                          <InfoTooltip content="The most recent date this investor submitted an offer or information request within the selected period." side="top" delayDuration={200}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/></svg></span>
                          </InfoTooltip>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordsRes.data.map((record, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>
                          {(drillDownPage - 1) * pageSize + idx + 1}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.investor_name}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.company_name ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{(record.markets ?? []).join(', ') || '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{record.offers_count}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{record.questions_count}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>
                          {record.last_engagement_date
                            ? new Date(record.last_engagement_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </InfoTooltip.Provider>

                {recordsRes.pagination && (
                  <div className={styles.paginationControls}>
                    <Text variant="bodyXs" color="muted">
                      Rows {((drillDownPage - 1) * pageSize + 1).toLocaleString()}–
                      {Math.min(drillDownPage * pageSize, recordsRes.pagination.total_count).toLocaleString()}
                      {' of '}
                      {recordsRes.pagination.total_count.toLocaleString()}
                    </Text>
                    <Stack direction="row" gap="8">
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || recordsRes.pagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || !recordsRes.pagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </div>
                )}
              </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
